/**
 * Seven-segment lamp patterns for the digits 0-9, one bit per segment in the
 * order [a, b, c, d, e, f, g] (a = top bar, b/c = right verticals top/bottom,
 * d = bottom bar, e/f = left verticals bottom/top, g = middle bar). 1 = lit.
 */
export const SEG_PATTERNS: readonly (readonly number[])[] = [
  [1, 1, 1, 1, 1, 1, 0],
  [0, 1, 1, 0, 0, 0, 0],
  [1, 1, 0, 1, 1, 0, 1],
  [1, 1, 1, 1, 0, 0, 1],
  [0, 1, 1, 0, 0, 1, 1],
  [1, 0, 1, 1, 0, 1, 1],
  [1, 0, 1, 1, 1, 1, 1],
  [1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 0, 1, 1],
];

/**
 * One frame of a display panel: `segments[i]` is the digit (0-9) shown at slot i
 * counting from the leftmost slot, or -1 for a blank (unlit) slot; `dp[i]` is the
 * decimal-point lamp at slot i.
 */
export interface DisplayFrame {
  segments: number[];
  dp: boolean[];
}

/**
 * Decomposes a display value into per-slot digits for an N-slot seven-segment
 * panel. Pure (no rounding of the upstream value — only the rendered fraction is
 * rounded). The sign is dropped (`|v|`); negatives are out of scope.
 *
 * Three regimes:
 * - Integer value fitting in N slots: right-aligned with blank (unlit) high slots
 *   and no decimal point, so 0 shows a single `0` and 42 shows `  42`.
 * - Integer part needing more than N slots: the low N digits (`int mod 10^N`)
 *   shown WITH leading zeros and no decimal point — the odometer wrap made
 *   readable (10001 in 4 slots → `0001`).
 * - Fractional value with spare slots: integer part left-aligned in the high
 *   slots, the decimal point on the last integer digit, and every spare low slot
 *   filled with the fraction rounded to that many places — trailing zeros kept,
 *   so 1.0001 in 4 slots → `1.000` (the DP and zeros stay lit to signal a
 *   fractional value even when it rounds to zero). When the integer part already
 *   fills all N slots the fraction is dropped (no DP). A fraction that rounds up
 *   into a new integer digit re-forms as the carried integer, still shown with a
 *   zero-filled fraction because the raw value was non-integer.
 */
export function decomposeDisplay(value: number, digits: number): DisplayFrame {
  const n = Math.max(1, Math.floor(digits));
  const segments = new Array<number>(n).fill(-1);
  const dp = new Array<boolean>(n).fill(false);
  const v = Number.isFinite(value) ? Math.abs(value) : 0;

  const writeRightAligned = (str: string): void => {
    const start = n - str.length;
    for (let i = 0; i < str.length; i++) {
      segments[start + i] = str.charCodeAt(i) - 48;
    }
  };
  const writeAll = (str: string): void => {
    for (let i = 0; i < n; i++) {
      segments[i] = str.charCodeAt(i) - 48;
    }
  };

  let intPart = Math.floor(v);
  let intStr = String(intPart);
  if (intStr.length > n) {
    writeAll(String(intPart % 10 ** n).padStart(n, "0"));
    return { segments, dp };
  }
  if (v === intPart) {
    writeRightAligned(intStr);
    return { segments, dp };
  }
  let fracSlots = n - intStr.length;
  if (fracSlots <= 0) {
    writeRightAligned(intStr);
    return { segments, dp };
  }
  let rounded = Math.round((v - intPart) * 10 ** fracSlots);
  if (rounded >= 10 ** fracSlots) {
    intPart += 1;
    intStr = String(intPart);
    if (intStr.length > n) {
      writeAll(String(intPart % 10 ** n).padStart(n, "0"));
      return { segments, dp };
    }
    fracSlots = n - intStr.length;
    if (fracSlots <= 0) {
      writeRightAligned(intStr);
      return { segments, dp };
    }
    rounded = 0;
  }
  let idx = 0;
  for (const ch of intStr) {
    segments[idx++] = ch.charCodeAt(0) - 48;
  }
  dp[idx - 1] = true;
  const fracStr = String(rounded).padStart(fracSlots, "0");
  for (const ch of fracStr) {
    if (idx >= n) {
      break;
    }
    segments[idx++] = ch.charCodeAt(0) - 48;
  }
  return { segments, dp };
}
